import * as React from 'react'
import classnames from 'classnames'
import { connect } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import Moment from 'react-moment'
import { RouteComponentProps, withRouter } from 'react-router'
import { Modal, Button, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'

import { RootState } from '../../store/rootReducer'
import { Employee } from '../../lib/interfaces/Employee.interface'
import { Termination } from '../../lib/interfaces/Termination.interface'
import { User, hasPermission } from '../../lib/interfaces/User.interface'

import Breadcrumbs from '../Breadcrumbs.component'
import LocationImage from '../../common/LocationImage.component'

import terminationReasons from '../../utils/termination-reason'

import * as appSelectors from '../../store/app/selectors'

import * as employeesActions from '../../store/employees/actions'
import * as terminationsActions from '../../store/terminations/actions'

const get = require('lodash/get')
const isEmpty = require('lodash/isEmpty')
const compact = require('lodash/compact')

interface RouteInfo {
  terminationId: string
}

export interface TerminationDetailsProps extends RouteComponentProps<RouteInfo> {
  user: User
  isLoading: boolean
  termination?: Termination
  setCurrentEmployee: (employee?: Employee) => void
  getLocationWithId: (locationId: number) => (Location | undefined)
  deleteTermination: (terminationId: string, next?: (err?: any) => void) => void
  fetchTermination: (terminationId: string, next?: (err?: any, termination?: Termination) => void) => void
  confirmTermination: (terminationId: string, next?: (err?: any, termination?: Termination) => void) => void
}

interface TerminationDetailsState {
  confirmationModalOpen: boolean
}

class TerminationDetails extends React.PureComponent<TerminationDetailsProps, TerminationDetailsState> {
  state = {
    confirmationModalOpen: false
  }

  get terminationId() {
    return this.props.match.params.terminationId
  }

  get terminationLocation() {
    const { termination, getLocationWithId } = this.props

    const locationId = get(termination, 'employee.location.id')
    const employeeLocation = locationId && getLocationWithId(locationId)
    return employeeLocation
  }

  componentDidMount() {
    this.props.fetchTermination(this.terminationId)
  }

  getEmployeeName(employee?: Employee) {
    if (!employee) { return null }
    return compact([ employee.firstName, employee.lastName ]).join(' ')
  }

  handleConfirmTermination() {
    const { confirmTermination } = this.props
    const terminationId = this.terminationId

    confirmTermination(terminationId, (err?: any, termination?: Termination) => {
      this.setState({ confirmationModalOpen: false })
    })
  }

  renderEmployee(employee?: Employee) {
    const employeeName = this.getEmployeeName(employee)

    if (isEmpty(employeeName)) { return null }
    return (
      <a
        className="cursor-pointer"
        onClick={e => {
          e.stopPropagation()
          this.props.setCurrentEmployee(employee!)
        }}
      >
        <u>{employeeName}</u>
      </a>
    )
  }

  renderTerminationConfirmationModal() {
    const { isLoading } = this.props
    const { confirmationModalOpen } = this.state

    return (
      <Modal isOpen={confirmationModalOpen}>
        <ModalHeader toggle={() => this.setState({ confirmationModalOpen: false })}>Confirm termination</ModalHeader>
        <ModalBody>
          Are you sure you want to confirm this termination?
        </ModalBody>
        <ModalFooter>
          <Button color="light" className="ggbutton" onClick={() => this.setState({ confirmationModalOpen: false })}>Cancel</Button>
          <Button color="primary" disabled={isLoading} className="ggbutton" onClick={() => this.handleConfirmTermination()}>
            {isLoading &&
              <FontAwesomeIcon
                spin
                className="mr-3"
                icon={['fas', 'spinner']}
              />
            }
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  render() {
    const { user, termination } = this.props

    const terminationLocation = this.terminationLocation

    return (
      <div>
        <Breadcrumbs breadcrumbs={[
          { name: 'Terminations', link: '/terminations' },
          { name: this.getEmployeeName(termination && termination.employee) || 'Unnamed Employee', active: true }
        ]} />

        {termination &&
          <div className="accident-page mt-4">
            {this.renderTerminationConfirmationModal()}
            <section className="container-fluid pl-0 pr-0 ml-0">
              <div className="d-flex align-items-center mb-2">
                <h5 className="title mb-0">{this.getEmployeeName(termination.employee) || 'Unnamed Employee'}</h5>
                {!termination.confirmed && hasPermission(user, 'ROLE_OFFICE_TERMINATE') &&
                  <React.Fragment>
                    <Button
                      size="sm"
                      color="light"
                      className="ggbutton ml-3"
                      onClick={() => this.props.history.push(`/terminations/${this.terminationId}/edit`)}
                    >
                        Edit
                    </Button>

                    <Button
                      size="sm"
                      color="dark"
                      className="ggbutton ml-3"
                      // onClick={() => this.props.deleteTermination(this.terminationId)}
                    >
                        Delete
                    </Button>

                    <Button
                      size="sm"
                      color="primary"
                      className="ggbutton ml-3"
                      onClick={() => this.setState({ confirmationModalOpen: true })}
                    >
                        Confirm
                    </Button>
                  </React.Fragment>
                }
              </div>

              <div className="row no-gutters mt-4">
                <div className="col-12 col-md-8 col-lg-5 pr-md-3">
                  <div className="ggcard p-3">
                    <table>
                      <tbody>
                        <tr>
                          <td className="pr-3">Employee:</td>
                          <td>{this.renderEmployee(termination.employee)}</td>
                        </tr>
                        <tr>
                          <td className="pr-3">Last day of work:</td>
                          <td>
                            {termination.lastDayOfWork
                              ? <Moment date={termination.lastDayOfWork} format="MM.DD.YYYY" />
                              : '-'
                            }
                          </td>
                        </tr>
                        <tr>
                          <td className="pr-3">Created at:</td>
                          <td>
                            {termination.dateCreated
                              ? <Moment date={termination.dateCreated} format="MM.DD.YYYY" />
                              : '-'
                            }
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {terminationLocation && (
                  <div className="col-12 col-md-4 col-lg-3 pr-lg-3 mt-3 mt-md-0">
                    <div className="ggcard h-100" style={{ minHeight: '125px' }}>
                      <LocationImage locationCode={termination.employee.location!.code} />
                    </div>
                  </div>
                )}

                <div className={classnames('col-12 col-md-8 col-lg-5 pr-md-3', { 'mt-3': !!terminationLocation })}>
                  <div className="ggcard p-3">
                    <table>
                      <tbody>
                        <tr>
                          <td className="pr-3">Confirmed:</td>
                          <td>{termination.confirmed ? 'Yes' : 'No'}</td>
                        </tr>
                        <tr>
                          <td className="pr-3">Voluntary:</td>
                          <td>{termination.voluntary ? 'Yes' : 'No'}</td>
                        </tr>
                        <tr>
                          <td className="pr-3">Eligible for rehire:</td>
                          <td>{termination.eligibleForRehire ? 'Yes' : 'No'}</td>
                        </tr>
                        <tr>
                          <td className="pr-3">Reason:</td>
                          <td>
                            {termination.reason ? terminationReasons[termination.reason] : '-'}
                          </td>
                        </tr>
                        <tr>
                          <td className="pr-3">Comments:</td>
                          <td>
                            {termination.comments ? termination.comments : '-'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </section>
          </div>
        }
      </div>
    )
  }
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
  isLoading: state.terminations.isLoading,
  termination: state.terminations.terminationDetails,
  getLocationWithId: appSelectors.getLocationWithId(state)
})

const mapDispatchToProps = (dispatch) => ({
  setCurrentEmployee: (employee?: Employee) => dispatch(employeesActions.setCurrentEmployee(employee)),
  deleteTermination: (terminationId: string, next?: (err?: any) => void) =>
    dispatch(terminationsActions.deleteTermination(terminationId, next)),
  fetchTermination: (terminationId: string, next?: (err?: any, termination?: Termination) => void) =>
    dispatch(terminationsActions.fetchTermination(terminationId, next)),
  confirmTermination: (terminationId: string, next?: (err?: any, termination?: Termination) => void) =>
    dispatch(terminationsActions.confirmTermination(terminationId, next))
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TerminationDetails))



// WEBPACK FOOTER //
// ./src/routes/terminations/TerminationDetails.page.tsx